import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  throw new Error('Missing REACT_APP_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable');
}

const projectRef = supabaseUrl.includes('supabase.co') 
  ? supabaseUrl.split('//')[1]?.split('.')[0] 
  : '';
// Create Supabase client with CORRECT authentication configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // FIXED: Explicitly configure localStorage with the correct key format
    // This ensures supabase.functions.invoke() can find the session token
    storage: window.localStorage,
    storageKey: projectRef ? `sb-${projectRef}-auth-token` : 'sb-auth-token',
  },
  global: {
    headers: {
      'X-Client-Info': 'omniclarix-web',
    },
  },
});

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper function to get user profile
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('active_profiles') // 👈 CHANGED from 'profiles' to read from your self-cleaning view
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) throw error;
  return data;
};

// Helper function to get user subscription
export const getUserSubscription = async (userId) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
  return data;
};

// Helper function to check if user has premium
export const checkIsPremium = async (userId) => {
  const { data, error } = await supabase
    .rpc('has_active_premium', { p_user_id: userId });
  
  if (error) throw error;
  return data || false;
};

// Helper function to get today's upload count
export const getTodayUploadCount = async (userId) => {
  const { data, error } = await supabase
    .rpc('get_today_upload_count', { p_user_id: userId });
  
  if (error) throw error;
  return data || 0;
};

// Helper function to check if user can upload
export const canUserUpload = async (userId, freeLimit = 3) => {
  const { data, error } = await supabase
    .rpc('can_user_upload', { 
      p_user_id: userId,
      p_free_limit: freeLimit 
    });
  
  if (error) throw error;
  return data || false;
};

// Helper function to get remaining uploads
export const getRemainingUploads = async (userId, freeLimit = 3) => {
  const { data, error } = await supabase
    .rpc('can_user_upload', { 
      p_user_id: userId,
      p_free_limit: freeLimit 
    });
  
  if (error) throw error;
  return data || 0;
};

// Helper function to increment upload count
export const incrementUploadCount = async (userId) => {
  const { data, error } = await supabase
    .rpc('increment_upload_count', { p_user_id: userId });
  
  if (error) throw error;
  return data;
};

// Helper function to get dashboard stats
export const getDashboardStats = async (userId) => {
  const { data, error } = await supabase
    .rpc('get_user_dashboard_stats', { p_user_id: userId });
  
  if (error) throw error;
  return data;
};

// Storage helper functions
export const uploadDocument = async (userId, file) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const filePath = `${userId}/${fileName}`;

  const { data, error } = await supabase.storage
    .from('documents')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;
  return data;
};

export const getDocumentUrl = (filePath) => {
  const { data } = supabase.storage
    .from('documents')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
};

export const getDocumentSignedUrl = async (filePath, expiresIn = 3600) => {
  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(filePath, expiresIn);

  if (error) throw error;
  return data.signedUrl;
};

export const deleteDocumentFile = async (filePath) => {
  const { data, error } = await supabase.storage
    .from('documents')
    .remove([filePath]);

  if (error) throw error;
  return data;
};

// Auth event listener helper
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};

// Sign out helper
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export default supabase;